import { useState, useEffect } from 'react';
import { FaShoppingCart, FaPlusCircle } from 'react-icons/fa';
import { MdRemoveCircle } from 'react-icons/md';
import { IoIosAddCircle } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { updateQuantity, removeFromCart, removeProductFromCart } from '../redux/cartSlice';
import { apiFetch, getProductById, getCurrentUser, MAIL_URL, fetchPincodeIndia } from '../api';
import './Checkout.css';
import ProductModal from '../components/ProductModal';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import ResultScreen from '../components/ResultScreen';

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector(state => state.cart);
  let user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');
  const getUserIdFrom = (u) => (u ? (u.id || u._id) : null);
  let userId = getUserIdFrom(user);

  const [isLoading, setIsLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFormData, setAddressFormData] = useState({});
  const [addressStep, setAddressStep] = useState('details');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFetchingPin, setIsFetchingPin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    const fetchUserAddresses = async () => {
      // checking token there or not
      if (!token) {
        toast.error("Please log in to proceed to checkout.");
        navigate('/login');
        return;
      }

      // Current user fetch from api, if user didnot failed to fetch locally
      if (!userId) {
        try {
          const me = await getCurrentUser();
          if (me && me.success && me.data) {
            user = me.data;
            localStorage.setItem('user', JSON.stringify(user));
            userId = getUserIdFrom(user);
          }
        } catch (err) {
          // Fetching failed handling
          toast.error('Session expired. Please log in again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login'); // redirect to log in because fetch fail
          return;
        }
      }

      // address fetching
      try {
        const res = await apiFetch(`/api/addresses/user/${userId}`);
        if (res.success && res.data.length > 0) {
          setAddresses(res.data);
          const defaultAddress = res.data.find(addr => addr.is_default) || res.data[0];
          setSelectedAddress(defaultAddress);
          setShowAddressForm(false);

          // Set address form to default address
        } else {
          setShowAddressForm(true);
        }
      } catch (error) {
        toast.error("Could not fetch addresses.");
        setShowAddressForm(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserAddresses();
  }, [userId, navigate]);


  // Remove cart confirmation
  const removeItem = (product) => {
    if (isToastVisible) return;

    setIsToastVisible(true);
    toast((t) => (
      <div style={{ padding: '8px 12px' }}>
        <div style={{ marginBottom: 8, fontWeight: 600 }}>Remove this item from cart?</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={async () => {
              try {
                if (localStorage.getItem('token')) {
                  await dispatch(removeProductFromCart(product));
                } else {
                  dispatch(removeFromCart(product.id));
                  window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Removed from cart', item: product }}));
                }
                toast.dismiss(t.id);
                setIsToastVisible(false);
              } catch (err) {
                console.error(err);
                toast.error('Failed to remove item.');
                toast.dismiss(t.id);
                setIsToastVisible(false);
              }
            }}
            style={{ background: '#82278A', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4 }}
          >Yes</button>
          <button onClick={() => {
            toast.dismiss(t.id);
            setIsToastVisible(false);
          }} style={{ background: '#eee', border: 'none', padding: '6px 10px', borderRadius: 4 }}>No</button>
        </div>
      </div>
    ), { 
        duration: Infinity,
        onDismiss: () => setIsToastVisible(false)
    });
  };

  // update cart handling
  const updateQuantityHandler = (id, newQuantity) => {
    if (newQuantity < 1) {
      dispatch(removeFromCart(id));
      return;
    }
    dispatch(updateQuantity({ id, quantity: newQuantity }));
  };

  const formatCurrency = (amount) => `₹${amount.toFixed(2)}`;

  // tax 10%
  const cartTotal = Number(cart.total || 0);
  const tax = Number((cartTotal * 0.1) || 0);
  const totalWithTax = Number((cartTotal + tax) || 0);

  const handleAddressFormChange = (e) => {
    setAddressFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };


  // Fetch pincode autofill city,states
  const fetchPincodeAndFill = async (pincode) => {
    if (!pincode || !/^[0-9]{6}$/.test(String(pincode))) {
      toast.error('Enter a valid PIN code (6 digits)');
      return;
    }
    setIsFetchingPin(true);
    try {
      const info = await fetchPincodeIndia(pincode);
      setAddressFormData(prev => ({ ...prev, city: info.district || prev.city, state: info.state || prev.state, country: 'India' }));
      toast.success('Auto-filled city/state from PIN');
      setAddressStep('details');
    } catch (err) {
      toast.error(err.message || 'Could not fetch PIN details');
    } finally {
      setIsFetchingPin(false);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    const url = addressFormData._id ? `/api/addresses/update/${addressFormData._id}` : '/api/addresses/create';
    const method = addressFormData._id ? 'PUT' : 'POST';
    try {
      // Map frontend form fields to backend Address schema
      // validation
      const validate = (data) => {
        if (!data.addressLine1 || !/\d/.test(data.addressLine1)) return 'Address Line 1 must include house/building number';
        if (!data.pincode || !/^[0-9]{6}$/.test(String(data.pincode))) return 'Pincode must be 6 digits';
        const containsNumber = s => /\d/.test(String(s || ''));
        if (containsNumber(data.city)) return 'City must not contain numbers';
        if (containsNumber(data.state)) return 'State must not contain numbers';
        if (containsNumber(data.country)) return 'Country must not contain numbers';
        return null;
      };

      const validationError = validate(addressFormData);
      if (validationError) {
        toast.error(validationError);
        setIsProcessing(false);
        return;
      }

      const payload = {
        user_id: userId,
        street: addressFormData.addressLine1 || addressFormData.street || null,
        city: addressFormData.city || null,
        state: addressFormData.state || null,
        pincode: addressFormData.pincode || null,
        country: addressFormData.country || null,
        phone: addressFormData.phone || null,
        is_default: !!addressFormData.is_default
      };
          // Address save handling
      const res = await apiFetch(url, { method, body: payload });
      if (!res.success) throw new Error(res.message);
      toast.success(res.message || "Address saved successfully!");
      const updatedAddressesRes = await apiFetch(`/api/addresses/user/${userId}`);
      if (updatedAddressesRes.success) {
        setAddresses(updatedAddressesRes.data);
        const newAddress = updatedAddressesRes.data.find(addr => addr._id === (res.data?._id || addressFormData._id)) || updatedAddressesRes.data[0];
        setSelectedAddress(newAddress);
        setShowAddressForm(false);
      }
    } catch (error) {
      toast.error(error.message || "Failed to save address.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Payment handling
  const handleProceedToPayment = async () => {
    if (!selectedAddress) {
      toast.error("Please select or add a shipping address.");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy.");
      return;
    }
    setIsProcessing(true);

    // Keep a reference to ourOrderId outside the handler
    let ourOrderId = null;

    try {

      // creating a temporary unique ID for the order
      const generateObjectId = () => {
        // simple 24-hex generator — acceptable for Mongo _id format
        let ts = Math.floor(new Date().getTime() / 1000).toString(16);
        while (ts.length < 8) ts = '0' + ts;
        let rand = '';
        for (let i = 0; i < 16; i++) rand += Math.floor(Math.random() * 16).toString(16);
        return ts + rand;
      };

      const tempOrderId = generateObjectId();

      // Create OrderItem records for each cart item
      const createdOrderItemPromises = cart.items.map(item =>
        apiFetch('/api/order-items/create', {
          method: 'POST',
          body: {
            order: tempOrderId,
            product: item.id,
            quantity: item.quantity,
            price: item.price
          }
        }).then(res => res.data || res)
      );

      const createdOrderItems = await Promise.all(createdOrderItemPromises);
      const orderItemIds = createdOrderItems.map(i => i._id || i.data?._id || i.data);

      // include tax (GST 10%) in order total
      const taxAmount = Number((cart.total * 0.1) || 0);
      const totalWithTax = Number((cart.total + taxAmount) || 0);
      const orderPayload = {
        user: userId,
        orderItems: orderItemIds,
        address: selectedAddress._id,
        total_amount: Number(totalWithTax.toFixed(2)),
        tax_amount: Number(taxAmount.toFixed(2))
      };
      // order creating internally
      const internalOrder = await apiFetch('/api/orders/create', { method: 'POST', body: orderPayload });

      ourOrderId = internalOrder?.order?._id; // Assign the ID here
      if (!ourOrderId) {
        throw new Error("Could not retrieve order ID after creation.");
      }

      // Razorpay order create
      const razorpayOrderData = await apiFetch('/api/payments/create', {
        method: 'POST',
        body: { orderId: ourOrderId }
      });

      const key_id = process.env.REACT_APP_RAZORPAY_KEY_ID;
      const { id: razorpayOrderId, amount } = razorpayOrderData.razorpayOrder;

      if (!key_id) {
        throw new Error("Razorpay Key ID is not configured in the .env file.");
      }

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "Label Aadvi",
        description: `Order ID: ${ourOrderId}`,
        order_id: razorpayOrderId,
        // THE FIX IS INSIDE THE HANDLER 
        handler: async function (response) {
          try {
            // Step 1: Verify the payment - include our internal order id so backend can map/update the order
            await apiFetch('/api/payments/verify', {
              method: 'POST',
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                orderId: ourOrderId
              },
              baseUrl: MAIL_URL,
            });

            // Show success screen
            setPaymentResult('success');

            // After 3 seconds, navigate
            setTimeout(() => {
                setPaymentResult(null);
                if (ourOrderId) {
                  navigate(`/ordertracking/${ourOrderId}`);
                } else {
                  // Fallback if order ID
                  navigate('/orderhistory');
                }
            }, 3000);

          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
            setPaymentResult('failed');
            setTimeout(() => {
                setPaymentResult(null);
                setIsProcessing(false); // Re-enable button on verification failure
            }, 7000);
          }
        },
        modal: {
            ondismiss: function() {
                setIsProcessing(false);
            }
        },
        //  This helps prefill user details in Razorpay form
        prefill: {
          name: `${user.firstName || ''} ${user.lastName || ''}`,
          email: user.email,
          contact: user.phone || selectedAddress.phone
        },
        notes: {
          address: `${selectedAddress.street || selectedAddress.addressLine1}, ${selectedAddress.city}`
        },
        theme: {
          color: "#82287A"
        }
      };
      // open razorpay model
      const rzp1 = new window.Razorpay(options);
      rzp1.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setPaymentResult('failed');
        setTimeout(() => {
            setPaymentResult(null);
            setIsProcessing(false); // Re-enable button on payment failure
        }, 3000);
      });
      rzp1.open();
    } catch (error) {
      toast.error("An error occurred: " + error.message);
      setIsProcessing(false); // Re-enable button if order creation fails
    }
  };

  return (
    <>
      {paymentResult && <ResultScreen status={paymentResult} />}
      <div className="container-fluid min-vh-100 checkout-container">
        <div className="row">
          {/* Order Summary Column */}
          <div className="col-lg-4 col-md-5 p-4 bg-white order-summary-col">
            <div className="order-summary">
              <h2 className="text-center mb-4 heading-primary">Your Order Summary</h2>
              {cart.items.length === 0 ? (
                <div className="text-center py-5"><FaShoppingCart size={48} className="text-muted mb-3" /><p>Your cart is empty</p></div>
              ) : (
                <>
                  {cart.items.map(item => (
                    <div key={item.id} className="card mb-3 border-0 shadow-sm position-relative">
                      <button className="btn-remove" onClick={() => removeItem(item)}>&times;</button>
                      <div className="card-body d-flex">
                        <img src={item.image} alt={item.title} className="item-image" style={{ cursor: "pointer" }} onClick={async () => {
                          try {
                            const fullProduct = await getProductById(item.id);
                            setSelectedProduct(fullProduct.data);
                            setModalOpen(true);
                          } catch (error) {
                            console.error('Failed to fetch product details:', error);
                          }
                        }} />
                        <div className="flex-grow-1 ms-3">
                          <h5 className="card-title" style={{ cursor: "pointer" }} onClick={async () => {
                            try {
                              const fullProduct = await getProductById(item.id);
                              setSelectedProduct(fullProduct.data);
                              setModalOpen(true);
                            } catch (error) {
                              console.error('Failed to fetch product details:', error);
                            }
                          }}>{item.title || item.name}</h5>
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="quantity-controls">
                              <button onClick={() => updateQuantityHandler(item.id, item.quantity - 1)}><MdRemoveCircle /></button>
                              <span>{item.quantity}</span>
                              <button onClick={() => updateQuantityHandler(item.id, item.quantity + 1)}><IoIosAddCircle /></button>
                            </div>
                            <div className="price-tag">{formatCurrency(item.price * item.quantity)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2"><span className="text-muted">Shipping:</span><span className="text-success">FREE</span></div>
                    <div className="d-flex justify-content-between align-items-center mb-2"><span className="text-muted">Tax (10%)</span><span className="text-muted">{formatCurrency(tax)}</span></div>
                    <hr />
                    <div className="d-flex justify-content-between align-items-center"><h4 className="mb-0 heading-primary">TOTAL:</h4><h4 className="mb-0 text-primary">{formatCurrency(totalWithTax)}</h4></div>
                  </div>
                </>
              )}
              <div className="mt-4"><Link to="/products" className="btn btn-primary w-100 py-2 custom-primary-btn">Continue Shopping</Link></div>
            </div>
          </div>

          {/* Shipping Address and Payment Column */}
          <div className="col-lg-8 col-md-7 p-4 checkout-section">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100"><Spinner /></div>
            ) : (
              <div className="card border-0 shadow">
                <div className="card-body  p-4">
                  {addresses.length > 0 && !showAddressForm ? (
                    <div>
                      <h2 className="mb-4 heading-primary">Select Shipping Address</h2>
                      {addresses.map(addr => (
                        <div key={addr._id} className={`address-item ${selectedAddress?._id === addr._id ? 'selected' : ''}`} onClick={() => setSelectedAddress(addr)}>
                          <input type="radio" name="address" checked={selectedAddress?._id === addr._id} readOnly />
                          <div className='address-text'>
                            <strong>{addr.street || addr.addressLine1}</strong><br />
                            {addr.city}, {addr.state} - {addr.pincode}<br />
                            Phone: {addr.phone}
                          </div>
                          <button className="btn-edit-address" onClick={(e) => {
                            e.stopPropagation();
                            setAddressFormData({
                              _id: addr._id,
                              addressLine1: addr.street || addr.addressLine1 || '',
                              city: addr.city || '',
                              state: addr.state || '',
                              pincode: addr.pincode || '',
                              country: addr.country || 'India',
                              phone: addr.phone || '',
                              is_default: !!addr.is_default
                            });
                            setAddressStep('details');
                            setShowAddressForm(true);
                          }}>Edit</button>
                        </div>
                      ))}
                      <button className="btn-add-new-address mt-3" onClick={() => { setAddressFormData({}); setAddressStep('pincode'); setShowAddressForm(true); }}>
                        <FaPlusCircle className="me-2" /> Add a New Address
                      </button>
                      <hr className="my-4" />
                      <div className="form-check mb-3">
                        <input className="form-check-input" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} id="termsCheck" />
                        <label className="form-check-label" htmlFor="termsCheck">
                          I have read and agree to the <Link to="/terms-and-conditions" target="_blank">Terms & Conditions</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>.
                        </label>
                      </div>
                      <button className="btn btn-primary w-100 py-3 custom-primary-btn" onClick={handleProceedToPayment} disabled={!selectedAddress || isProcessing || cart.items.length === 0 || !termsAccepted}>
                        {isProcessing ? 'Processing...' : `Deliver Here & Proceed to Pay`}
                      </button>
                    </div>
                  ) : (
                    <div>
                      {addressStep === 'pincode' ? (
                        <div>
                          <h2 className="mb-4 heading-primary">Enter Pincode to continue</h2>
                          <div className="row">
                            <div className="col-12 mb-3">
                              <label className="form-label">Pincode</label>
                              <input type="text" className="form-control" name="pincode" value={addressFormData.pincode || ''} onChange={handleAddressFormChange} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchPincodeAndFill(addressFormData.pincode); } }} required />
                            </div>
                            <div className="col-12 d-flex gap-2">
                              <button className="btn btn-secondary" onClick={() => { setShowAddressForm(false); setAddressStep('details'); }}>Cancel</button>
                              <button className="btn btn-primary" onClick={() => fetchPincodeAndFill(addressFormData.pincode)}>Continue</button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h2 className="mb-4 heading-primary">{addressFormData._id ? 'Edit Address' : 'Enter Shipping Address'}</h2>
                          <form onSubmit={handleSaveAddress}>
                            <div className="row">
                              <div className="col-12 mb-3"><label className="form-label">Address Line 1</label><input type="text" className="form-control" name="addressLine1" value={addressFormData.addressLine1 || ''} onChange={handleAddressFormChange} required /></div>
                              <div className="col-md-6 mb-3"><label className="form-label">City</label><input type="text" className="form-control" name="city" value={addressFormData.city || ''} onChange={handleAddressFormChange} required /></div>
                              <div className="col-md-6 mb-3"><label className="form-label">State</label><input type="text" className="form-control" name="state" value={addressFormData.state || ''} onChange={handleAddressFormChange} required /></div>
                              <div className="col-md-6 mb-3"><label className="form-label">Pincode</label><input type="text" className="form-control" name="pincode" value={addressFormData.pincode || ''} onChange={handleAddressFormChange} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchPincodeAndFill(addressFormData.pincode); } }} required /></div>
                              <div className="col-md-6 mb-3"><label className="form-label">Country</label><input type="text" className="form-control" name="country" value={addressFormData.country || 'India'} onChange={handleAddressFormChange} required /></div>
                              <div className="col-md-6 mb-3"><label className="form-label">Phone</label><input type="tel" className="form-control" name="phone" value={addressFormData.phone || ''} onChange={handleAddressFormChange} required /></div>
                              <div className="col-12 mt-3 d-flex gap-2">
                                <button type="submit" className="btn btn-primary w-100 py-3 custom-primary-btn" disabled={isProcessing}>{isProcessing ? 'Saving...' : 'Save Address'}</button>
                                {addresses.length > 0 && <button type="button" className="btn btn-secondary w-50 py-3" onClick={() => setShowAddressForm(false)}>Cancel</button>}
                              </div>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ProductModal post={selectedProduct || {}} isOpen={modalOpen} onClose={() => setModalOpen(false)} initialSelectedColor={selectedProduct?.selectedColor} initialSelectedSize={selectedProduct?.selectedSize} isWishlistPage={false} />
    </>
  );
};

export default CheckoutPage;